/**
 * GanttTimeline Component - Main timeline view with drag and drop
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, GripHorizontal, Circle } from 'lucide-react';
import { DndContext, DragOverlay, useDraggable } from '@dnd-kit/core';
import type { DragStartEvent, DragMoveEvent, DragEndEvent } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { STATUS_CONFIG, getStatusLabel } from '../../constants/status';
import type { GanttJob } from '../../hooks/useGanttJobs';
import { minutesToTime, timeToMinutes, getPosition, getWidth } from '../../hooks/useGanttDragDrop';

// Helper function to count unique clients across jobs in a group
const countUniqueClients = (jobs: GanttJob[]): number => {
  const clientIds = new Set<number>();
  jobs.forEach(job => {
    if (job.clientes && job.clientes.length > 0) {
      job.clientes.forEach(c => clientIds.add(c.id));
    } else if (job.cliente_id) {
      clientIds.add(job.cliente_id);
    }
  });
  return clientIds.size;
};

// Helper function to count unique hospitals across jobs in a group
const countUniqueHospitals = (jobs: GanttJob[]): number => {
  const hospitalIds = new Set<number>();
  jobs.forEach(job => {
    if (job.hospital_id) {
      hospitalIds.add(job.hospital_id);
    }
  });
  return hospitalIds.size;
};

// Helper function to build enhanced tooltip text
const buildJobTooltip = (job: GanttJob, horaInicio: string, horaFim: string): string => {
  const lines: string[] = [];

  // Line 1: Hospital - Title
  lines.push(`${job.hospital_nome} - ${job.titulo}`);

  // Line 2: Nome Fantasia do Hospital (if different from nome)
  if (job.hospital_nome_fantasia && job.hospital_nome_fantasia !== job.hospital_nome) {
    lines.push(`Nome Fantasia: ${job.hospital_nome_fantasia}`);
  }

  // Line 3: Status
  const statusLabel = getStatusLabel(job.status);
  lines.push(`Status: ${statusLabel}`);

  // Line 4: Clients - use nome_fantasia when available
  if (job.clientes && job.clientes.length > 0) {
    const clientNames = job.clientes.map(c => c.nome_fantasia || c.razao_social).join(', ');
    lines.push(`Clientes: ${clientNames}`);
  } else if (job.cliente_nome_fantasia) {
    lines.push(`Cliente: ${job.cliente_nome_fantasia}`);
  } else if (job.cliente_razao_social) {
    lines.push(`Cliente: ${job.cliente_razao_social}`);
  }

  // Line 5: Time range
  lines.push(`Horário: ${horaInicio} - ${horaFim}`);

  return lines.join('\n');
};

const AVATAR_COLORS = [
  'bg-purple-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500',
  'bg-red-500', 'bg-indigo-500', 'bg-pink-500', 'bg-teal-500'
];

interface ResizingJob {
  job: GanttJob;
  handle: 'left' | 'right';
  previewStart?: number;
  previewDuration?: number;
}

// JobBarOverlay - Preview for DragOverlay
const JobBarOverlay = ({
  job,
  dragPreviewPosition,
  timelinePixelWidth,
  startHour,
  totalHours
}: {
  job: GanttJob;
  dragPreviewPosition: number | null;
  timelinePixelWidth: number;
  startHour: number;
  totalHours: number;
}) => {
  const StatusIcon = STATUS_CONFIG[job.status]?.icon || Circle;
  const horaInicio = dragPreviewPosition !== null
    ? minutesToTime(dragPreviewPosition)
    : (job.hora_inicio || '07:00');
  const duracao = job.duracao || 120;
  const horaFim = minutesToTime(timeToMinutes(horaInicio) + duracao);

  const totalMinutes = totalHours * 60;
  const pixelWidth = timelinePixelWidth > 0
    ? (duracao / totalMinutes) * timelinePixelWidth
    : 120;

  return (
    <div
      className={`h-10 rounded shadow-lg ${STATUS_CONFIG[job.status]?.color || 'bg-gray-500 text-white'} ring-2 ring-blue-400 cursor-grabbing`}
      style={{
        width: `${Math.max(pixelWidth, 60)}px`,
        touchAction: 'none',
      }}
    >
      <div className="px-1.5 py-0.5 h-full flex flex-col justify-center text-[10px]">
        <div className="font-semibold truncate flex items-center gap-0.5">
          <StatusIcon className="w-2.5 h-2.5 flex-shrink-0" />
          <span className="truncate">{job.hospital_nome}</span>
        </div>
        <div className="text-[9px] opacity-80 truncate">
          {horaInicio} - {horaFim}
        </div>
      </div>
    </div>
  );
};

// DraggableJobBar Component
const DraggableJobBar = ({
  job,
  resizingJob,
  handleResizeStart,
  startHour,
  totalHours
}: {
  job: GanttJob;
  resizingJob: ResizingJob | null;
  handleResizeStart: (e: React.MouseEvent, job: GanttJob, handle: 'left' | 'right') => void;
  startHour: number;
  totalHours: number;
}) => {
  const navigate = useNavigate();
  const isAnyResizing = resizingJob !== null;

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: job.id,
    disabled: isAnyResizing,
  });

  const StatusIcon = STATUS_CONFIG[job.status]?.icon || Circle;
  const horaInicio = job.hora_inicio || '07:00';
  const duracao = job.duracao || 120;

  const isResizing = resizingJob?.job.id === job.id;
  const displayHoraInicio = isResizing && resizingJob?.previewStart !== undefined
    ? minutesToTime(resizingJob.previewStart)
    : horaInicio;

  const displayDuracao = isResizing && resizingJob?.previewDuration !== undefined
    ? resizingJob.previewDuration
    : duracao;

  const displayHoraFim = minutesToTime(timeToMinutes(displayHoraInicio) + displayDuracao);

  const position = getPosition(displayHoraInicio, startHour, totalHours);
  const width = getWidth(displayDuracao / 60, totalHours);

  // Hide items outside visible range
  const startMinutes = timeToMinutes(displayHoraInicio);
  const endMinutes = startMinutes + displayDuracao;
  const visibleStartMinutes = startHour * 60;
  const visibleEndMinutes = (startHour + totalHours) * 60;

  if (endMinutes < visibleStartMinutes || startMinutes > visibleEndMinutes) {
    return null;
  }

  const style: React.CSSProperties = {
    left: `${position}%`,
    width: `${width}%`,
    minWidth: '60px',
    opacity: isDragging ? 0.3 : 1,
    transform: !isResizing ? CSS.Translate.toString(transform) : undefined,
    touchAction: 'none',
  };

  return (
    <div
      ref={setNodeRef}
      className={`absolute top-1 h-10 rounded shadow-sm hover:shadow-md hover:z-20 group cursor-grab active:cursor-grabbing ${STATUS_CONFIG[job.status]?.color || 'bg-gray-500 text-white'} ${isResizing ? 'ring-2 ring-green-400 z-30' : ''} ${isDragging ? 'z-10' : ''}`}
      style={style}
      title={buildJobTooltip(job, displayHoraInicio, displayHoraFim)}
      {...attributes}
      {...listeners}
      onClick={() => !isDragging && !isResizing && navigate(`/cloud/contagens/${job.id}`)}
    >
      <div
        data-no-dnd="true"
        className="absolute left-0 top-0 bottom-0 w-5 cursor-ew-resize opacity-0 group-hover:opacity-100 hover:bg-white/30 active:bg-white/50 flex items-center justify-center z-20"
        onPointerDown={(e) => {
          e.stopPropagation();
          handleResizeStart(e as any, job, 'left');
        }}
        title="Arraste para ajustar início"
      >
        <GripHorizontal className="w-3 h-3" />
      </div>

      <div className="mx-4 h-full flex flex-col justify-center text-[10px]">
        <div className="font-semibold truncate flex items-center gap-0.5">
          <StatusIcon className="w-2.5 h-2.5 flex-shrink-0" />
          <span className="truncate">{job.hospital_nome}</span>
        </div>
        <div className="text-[9px] opacity-80 truncate">
          {displayHoraInicio} - {displayHoraFim}
        </div>
      </div>

      <div
        data-no-dnd="true"
        className="absolute right-0 top-0 bottom-0 w-5 cursor-ew-resize opacity-0 group-hover:opacity-100 hover:bg-white/30 active:bg-white/50 flex items-center justify-center z-20"
        onPointerDown={(e) => {
          e.stopPropagation();
          handleResizeStart(e as any, job, 'right');
        }}
        title="Arraste para ajustar duração"
      >
        <GripHorizontal className="w-3 h-3" />
      </div>
    </div>
  );
};

interface AuditorGroup {
  auditor: string;
  jobs: GanttJob[];
}

interface GanttTimelineProps {
  loading: boolean;
  filteredJobs: GanttJob[];
  jobs: GanttJob[];
  startHour: number;
  totalHours: number;
  timelineRef: React.RefObject<HTMLDivElement>;
  timelinePixelWidth: number;
  sensors: ReturnType<typeof import('@dnd-kit/core').useSensors>;
  activeId: number | null;
  dragPreviewPosition: number | null;
  resizingJob: ResizingJob | null;
  onDragStart: (event: DragStartEvent) => void;
  onDragMove: (event: DragMoveEvent) => void;
  onDragEnd: (event: DragEndEvent) => Promise<void>;
  onResizeStart: (e: React.MouseEvent, job: GanttJob, handle: 'left' | 'right') => void;
}

export const GanttTimeline: React.FC<GanttTimelineProps> = ({
  loading,
  filteredJobs,
  jobs,
  startHour,
  totalHours,
  timelineRef,
  timelinePixelWidth,
  sensors,
  activeId,
  dragPreviewPosition,
  resizingJob,
  onDragStart,
  onDragMove,
  onDragEnd,
  onResizeStart,
}) => {
  const hours = Array.from({ length: totalHours }, (_, i) => startHour + i);

  // Group jobs by auditor
  const auditorGroups: Record<string, AuditorGroup> = {};
  filteredJobs.forEach(job => {
    const auditorNome = job.operador_nome;
    if (!auditorGroups[auditorNome]) {
      auditorGroups[auditorNome] = { auditor: auditorNome, jobs: [] };
    }
    auditorGroups[auditorNome].jobs.push(job);
  });

  const auditorList = Object.values(auditorGroups);

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
  };

  const getAvatarColor = (index: number) => {
    return AVATAR_COLORS[index % AVATAR_COLORS.length];
  };

  const activeJob = activeId ? jobs.find(j => j.id === activeId) : null;

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="text-center py-20">
          <p className="text-gray-500 dark:text-gray-400">Carregando...</p>
        </div>
      </div>
    );
  }

  if (filteredJobs.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="text-center py-20 text-gray-400 dark:text-gray-500">
          <Calendar className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p className="text-lg">Nenhuma auditoria agendada para este dia</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
      <DndContext
        sensors={sensors}
        modifiers={[restrictToHorizontalAxis]}
        onDragStart={onDragStart}
        onDragMove={onDragMove}
        onDragEnd={onDragEnd}
      >
        {/* Gantt Table */}
        <table className="w-full table-fixed">
          <thead>
            <tr className="border-b border-gray-300 dark:border-gray-600">
              {/* Fixed Auditor Column */}
              <th className="w-[220px] sticky left-0 bg-white dark:bg-gray-800 z-20 px-3 py-2 text-left border-r border-dashed border-gray-300 dark:border-gray-600">
                <div className="font-semibold text-gray-700 dark:text-gray-300 text-sm">Auditor</div>
              </th>
              {/* Hour Columns */}
              {hours.map(hour => (
                <th
                  key={hour}
                  className="text-center text-[10px] text-gray-400 dark:text-gray-500 border-l border-dashed border-gray-200 dark:border-gray-700 first:border-l-0 px-0.5 py-2 min-w-[60px]"
                >
                  {String(hour).padStart(2, '0')}h
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {auditorList.map((group, index) => (
              <tr key={group.auditor} className="border-b border-dashed border-gray-200 dark:border-gray-700">
                {/* Fixed Auditor Cell */}
                <td className="sticky left-0 bg-white dark:bg-gray-800 z-10 px-3 py-2 border-r border-dashed border-gray-300 dark:border-gray-600">
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-full ${getAvatarColor(index)} text-white flex items-center justify-center font-bold text-xs mr-2 flex-shrink-0`}>
                      {getInitials(group.auditor)}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900 dark:text-white text-sm truncate">{group.auditor}</div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-400">
                        {group.jobs.length} Cont., {countUniqueClients(group.jobs)} {countUniqueClients(group.jobs) !== 1 ? 'Clientes' : 'Cliente'}, {countUniqueHospitals(group.jobs)} Hosp.
                      </div>
                    </div>
                  </div>
                </td>
                {/* Timeline Cell */}
                <td colSpan={totalHours} className="p-0">
                  <div ref={timelineRef} className="relative h-12 w-full">
                    {/* Grid Lines */}
                    <div className="absolute inset-0 flex">
                      {hours.map(hour => (
                        <div key={hour} className="flex-1 border-l border-dashed border-gray-200 dark:border-gray-700 opacity-50 first:border-l-0"></div>
                      ))}
                    </div>

                    {/* Event Bars */}
                    {group.jobs.map(job => (
                      <DraggableJobBar
                        key={job.id}
                        job={job}
                        resizingJob={resizingJob}
                        handleResizeStart={onResizeStart}
                        startHour={startHour}
                        totalHours={totalHours}
                      />
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* DragOverlay */}
        <DragOverlay dropAnimation={null}>
          {activeJob ? (
            <JobBarOverlay
              job={activeJob}
              dragPreviewPosition={dragPreviewPosition}
              timelinePixelWidth={timelinePixelWidth}
              startHour={startHour}
              totalHours={totalHours}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};
